import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { ArkikAlertService } from '@shared/components/arkik-alert/arkik-alert.service';
import { TranslationService } from '@core/services/common/translation.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  PlantOfflineAlertService,
  PlantOfflineAlertConfig,
  AlertAction,
} from '@shared/services/plant/plant-offline-alert.service';

@Component({
  selector: 'app-offline-alert',
  templateUrl: './arkik-offline-alert.component.html',
  styleUrls: ['./arkik-offline-alert.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ArkikOfflineAlertComponent implements OnInit, OnDestroy {
  // ----Declare input/output params----------------//
  @Output() public closeModal: EventEmitter<number> = new EventEmitter();

  @Input() public set showModal(value: boolean) {
    this._openDialog = value;
    this.cd.markForCheck();
  }
  @Input() public type: 'warning' | 'error' = 'warning';
  @Input() public set message(value: string) {
    this._displayMessage = value;
    this.cd.markForCheck();
  }

  // ----Declare public READ-ONLY variables---------//
  private _openDialog: boolean;
  public get openDialog(): boolean {
    return this._openDialog;
  }

  private _displayMessage: string;
  public get displayMessage(): string {
    return this._displayMessage;
  }

  private _plantCode: string;
  public get plantCode(): string {
    return this._plantCode;
  }

  // Custom actions support
  private _actions: AlertAction[] = [];
  public get actions(): AlertAction[] {
    return this._actions;
  }

  // ----Declare private variables------------------//
  private _destroy$ = new Subject();

  constructor(
    private cd: ChangeDetectorRef,
    private a: ArkikAlertService,
    private t: TranslationService,
    private plantOfflineAlertService: PlantOfflineAlertService,
  ) {}

  ngOnInit(): void {
    // Initialize dialog as open when created via portal
    this._openDialog = true;
    this.cd.markForCheck();

    // subscribe to show alert notifications
    this.plantOfflineAlertService.showAlert$
      .pipe(takeUntil(this._destroy$))
      .subscribe((config: PlantOfflineAlertConfig) => {
        if (!config) {
          return;
        }

        this._displayMessage = config.message;
        this.type = config.type || 'warning';
        this._plantCode = config.plantCode || '';

        // Handle custom actions if provided
        if (config.actions && config.actions.length > 0) {
          this._actions = config.actions;
        } else {
          this._actions = [];
        }

        this.cd.markForCheck();
      });

    // subscribe to close alert notifications
    this.plantOfflineAlertService.closeAlert$.pipe(takeUntil(this._destroy$)).subscribe(() => {
      this._openDialog = false;
      this.cd.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  cwcCancel(): void {
    this._openDialog = false;
    this.closeModal.emit();
    this.cd.markForCheck();
  }

  cwcConfirm(): void {
    this._openDialog = false;
    this.plantOfflineAlertService.confirmAction();
    this.cd.markForCheck();
  }

  /**
   * Executes a custom action and closes the dialog
   * @param action The action to execute
   */
  executeAction(action: AlertAction): void {
    this._openDialog = false;
    if (action && action.action) {
      action.action();
    }
    this.cd.markForCheck();
  }
}
